import { Bell, Mail } from "lucide-react"
import { useTranslation } from "@/clientWeb/i18n/LanguageContext"
import type { StepProps, WebinarFormData } from "./types"

function Toggle({ checked, onChange, disabled }: { checked: boolean; onChange: (v: boolean) => void; disabled?: boolean }) {
  return (
    <button type="button" role="switch" aria-checked={checked} onClick={() => !disabled && onChange(!checked)}
      className={`relative inline-flex h-6 w-11 shrink-0 rounded-full transition-colors ${disabled ? "opacity-40 cursor-not-allowed" : "cursor-pointer"} ${checked ? "bg-primary" : "bg-slate-300 dark:bg-slate-600"}`}>
      <span className={`inline-block h-5 w-5 mt-0.5 rounded-full bg-card shadow transition-transform ${checked ? "translate-x-5.5" : "translate-x-0.5"}`} />
    </button>
  )
}

const reminderMinutes = [
  { value: 10, label: "10 minutes" },
  { value: 20, label: "20 minutes" },
  { value: 30, label: "30 minutes" },
  { value: 40, label: "40 minutes" },
]

const postWebinarTimes = [
  { value: 2, label: "2 minutes" },
  { value: 5, label: "5 minutes" },
]

type ReminderKey = "firstReminder" | "secondReminder" | "thirdReminder"

const reminderLabels: Record<ReminderKey, string> = {
  firstReminder: "1st Reminder",
  secondReminder: "2nd Reminder",
  thirdReminder: "3rd Reminder",
}

export function StepNotifications({ data, onChange }: StepProps) {
  const t = useTranslation()
  const { reminders, postWebinar, withRegistration } = data

  const handleReminderToggle = (key: ReminderKey, enabled: boolean) => {
    const updated = { ...reminders }
    if (!enabled) {
      if (key === "firstReminder") {
        updated.firstReminder = { enabled: false, time: 0 }
        updated.secondReminder = { enabled: false, time: 0 }
        updated.thirdReminder = { enabled: false, time: 0 }
      } else if (key === "secondReminder") {
        updated.secondReminder = { enabled: false, time: 0 }
        updated.thirdReminder = { enabled: false, time: 0 }
      } else {
        updated.thirdReminder = { enabled: false, time: 0 }
      }
    } else {
      updated[key] = { enabled: true, time: 10 }
      if (key === "secondReminder") { updated.thirdReminder = { enabled: false, time: 0 } }
    }
    onChange({ reminders: updated })
  }

  const handleReminderTime = (key: ReminderKey, time: number) => {
    const updated = { ...reminders, [key]: { ...reminders[key], time } }
    onChange({ reminders: updated })
  }

  const handlePostWebinarToggle = (key: keyof WebinarFormData["postWebinar"], enabled: boolean) => {
    onChange({ postWebinar: { ...postWebinar, [key]: { ...postWebinar[key], enabled } } })
  }

  const handlePostWebinarTime = (key: keyof WebinarFormData["postWebinar"], time: number) => {
    onChange({ postWebinar: { ...postWebinar, [key]: { ...postWebinar[key], time } } })
  }

  const isSecondDisabled = !reminders.firstReminder.enabled
  const isThirdDisabled = !reminders.secondReminder.enabled

  return (
    <div className="space-y-5">
      {/* Pre-webinar reminders – only when registration is enabled */}
      {withRegistration && (
        <div className="rounded-xl border border-stroke-100 dark:border-border dark:border-border p-5 space-y-4">
          <div>
            <div className="flex items-center gap-2 mb-0.5">
              <Bell className="w-4 h-4 text-primary" />
              <p className="font-semibold text-slate-900 dark:text-foreground/80 dark:text-white">{t("webinar.preWebinarReminders")}</p>
            </div>
            <p className="text-xs text-slate-500 dark:text-slate-400">{t("webinar.sendReminderEmails")}</p>
          </div>

          {(["firstReminder", "secondReminder", "thirdReminder"] as ReminderKey[]).map((key) => {
            const reminder = reminders[key]
            const disabled = key === "secondReminder" ? isSecondDisabled : key === "thirdReminder" ? isThirdDisabled : false
            return (
              <div key={key} className={`rounded-lg border p-4 space-y-3 transition-opacity ${disabled ? "opacity-50" : ""} border-stroke-100 dark:border-border dark:border-border`}>
                <div className="flex items-center justify-between">
                  <p className="text-sm font-medium text-slate-800 dark:text-white">{reminderLabels[key]}</p>
                  <Toggle checked={reminder.enabled} onChange={(v) => handleReminderToggle(key, v)} disabled={disabled} />
                </div>
                {reminder.enabled && (
                  <div className="flex items-center gap-3">
                    <label className="text-xs text-slate-500 dark:text-slate-400 shrink-0">{t("webinar.send")}</label>
                    <select
                      value={reminder.time}
                      onChange={(e) => handleReminderTime(key, parseInt(e.target.value))}
                      className="h-9 rounded-lg border border-stroke-100 dark:border-border dark:border-border bg-card dark:bg-card text-slate-900 dark:text-foreground/80 dark:text-white px-3 text-sm focus:outline-none focus:ring-2 focus:ring-primary/50"
                    >
                      {reminderMinutes.map(({ value, label }) => (
                        <option key={value} value={value}>{label}</option>
                      ))}
                    </select>
                    <label className="text-xs text-slate-500 dark:text-slate-400">{t("webinar.beforeStart")}</label>
                  </div>
                )}
              </div>
            )
          })}
        </div>
      )}

      {/* Post-webinar emails */}
      <div className="rounded-xl border border-stroke-100 dark:border-border dark:border-border p-5 space-y-4">
        <div>
          <div className="flex items-center gap-2 mb-0.5">
            <Mail className="w-4 h-4 text-primary" />
            <p className="font-semibold text-slate-900 dark:text-foreground/80 dark:text-white">{t("webinar.postWebinarEmails")}</p>
          </div>
          <p className="text-xs text-slate-500 dark:text-slate-400">{t("webinar.automatedEmailsAfterEnd")}</p>
        </div>

        {/* Thank You Email */}
        <div className="rounded-lg border border-stroke-100 dark:border-border dark:border-border p-4 space-y-3">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-slate-800 dark:text-white">{t("webinar.thankYouEmail")}</p>
              <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">{t("webinar.sentToAttendeesJoined")}</p>
            </div>
            <Toggle checked={postWebinar.thankYouEmail.enabled}
              onChange={(v) => handlePostWebinarToggle("thankYouEmail", v)} />
          </div>
          {postWebinar.thankYouEmail.enabled && (
            <div className="flex items-center gap-3">
              <label className="text-xs text-slate-500 dark:text-slate-400 shrink-0">Send</label>
              <select
                value={postWebinar.thankYouEmail.time}
                onChange={(e) => handlePostWebinarTime("thankYouEmail", parseInt(e.target.value))}
                className="h-9 rounded-lg border border-stroke-100 dark:border-border dark:border-border bg-card dark:bg-card text-slate-900 dark:text-foreground/80 dark:text-white px-3 text-sm focus:outline-none focus:ring-2 focus:ring-primary/50"
              >
                {postWebinarTimes.map(({ value, label }) => (
                  <option key={value} value={value}>{label}</option>
                ))}
              </select>
              <label className="text-xs text-slate-500 dark:text-slate-400">{t("webinar.afterWebinarEnds")}</label>
            </div>
          )}
        </div>

        {/* Follow-up Email – only when registration enabled */}
        {withRegistration && (
          <div className="rounded-lg border border-stroke-100 dark:border-border dark:border-border p-4 space-y-3">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-slate-800 dark:text-white">{t("webinar.followUpEmail")}</p>
                <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">{t("webinar.sentToRegistrantsNotAttend")}</p>
              </div>
              <Toggle checked={postWebinar.followUpEmail.enabled}
                onChange={(v) => handlePostWebinarToggle("followUpEmail", v)} />
            </div>
            {postWebinar.followUpEmail.enabled && (
              <div className="flex items-center gap-3">
                <label className="text-xs text-slate-500 dark:text-slate-400 shrink-0">Send</label>
                <select
                  value={postWebinar.followUpEmail.time}
                  onChange={(e) => handlePostWebinarTime("followUpEmail", parseInt(e.target.value))}
                  className="h-9 rounded-lg border border-stroke-100 dark:border-border dark:border-border bg-card dark:bg-card text-slate-900 dark:text-foreground/80 dark:text-white px-3 text-sm focus:outline-none focus:ring-2 focus:ring-primary/50"
                >
                  {postWebinarTimes.map(({ value, label }) => (
                    <option key={value} value={value}>{label}</option>
                  ))}
                </select>
                <label className="text-xs text-slate-500 dark:text-slate-400">{t("webinar.afterWebinarEnds")}</label>
              </div>
            )}
          </div>
        )}
      </div>

      {!withRegistration && (
        <div className="rounded-xl border border-stroke-100 dark:border-border/60 dark:border-slate-800 bg-slate-50/60 dark:bg-card/30 p-4">
          <p className="text-sm text-slate-500 dark:text-slate-400">
            {t("webinar.preWebinarRemindersAvailable")}
          </p>
        </div>
      )}
    </div>
  )
}
